import React, { useMemo, useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import dayjs from "dayjs";
import "./PostItTimelineView.css";

const PostItTimelineView = ({
  data,
  type,
  selectedUser,
  enableStickyHeader = false,
}) => {
  console.log("🚀 PostItTimelineView mounting with props:", {
    enableStickyHeader,
    selectedUser,
    type,
    dataLength: data?.length || 0,
  });

  // Refs for scroll synchronization
  const timelineHeaderRef = useRef(null);
  const timelineBodyRef = useRef(null);
  const timelineContainerRef = useRef(null);
  const stickySentinelRef = useRef(null);

  // State for sticky header
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [headerElement, setHeaderElement] = useState(null);

  // Debug state changes
  useEffect(() => {
    console.log("🔄 isHeaderSticky state changed to:", isHeaderSticky);
  }, [isHeaderSticky]);
  // Generate timeline days (1 week before today + 3 weeks after = 4 week window)
  const timelineData = useMemo(() => {
    const startDate = dayjs().subtract(1, "week").startOf("day");
    const endDate = dayjs().add(3, "week").endOf("day");
    const totalDays = endDate.diff(startDate, "day") + 1;

    // Calculate responsive day width
    // Desktop: 40% screen width for 4 weeks = 10% per week = ~1.43% per day
    // Mobile: 95% screen width for 4 weeks = 23.75% per week = ~3.39% per day
    const isMobile = window.innerWidth <= 768;
    const weekPercentage = isMobile ? 23.75 : 10; // Percentage of screen width per week
    const dayPercentage = weekPercentage / 7; // Percentage per day
    const pixelsPerDay = Math.max(
      120,
      (window.innerWidth * dayPercentage) / 100
    ); // Minimum 120px

    const days = [];
    for (let i = 0; i < totalDays; i++) {
      const currentDay = startDate.add(i, "day");
      days.push({
        date: currentDay,
        dayNumber: currentDay.date(),
        monthName: currentDay.format("MMM"),
        isToday: currentDay.isSame(dayjs(), "day"),
        isWeekend: currentDay.day() === 0 || currentDay.day() === 6,
        position: i * pixelsPerDay,
      });
    }

    return {
      startDate,
      endDate,
      totalDays,
      days,
      pixelsPerDay,
      totalWidth: totalDays * pixelsPerDay,
      dayPercentage,
    };
  }, []);

  // Helper function to calculate post-it position and size
  const calculatePostItPosition = (item) => {
    const startDate = dayjs(item.createdDate);
    const endDate = dayjs(item.expectedDate);

    // Calculate start position relative to timeline start
    const startOffset = startDate.diff(timelineData.startDate, "day");
    const duration = Math.max(1, endDate.diff(startDate, "day") + 1);

    // For tickets that start before our timeline, adjust the start position and duration
    let adjustedStartOffset = startOffset;
    let adjustedDuration = duration;

    if (startOffset < 0) {
      // Ticket starts before our timeline - show it starting from day 0
      adjustedStartOffset = 0;
      // Calculate how much of the ticket's duration is visible in our timeline
      const visibleEndDate = endDate.isBefore(timelineData.endDate)
        ? endDate
        : timelineData.endDate;
      adjustedDuration = Math.max(
        1,
        visibleEndDate.diff(timelineData.startDate, "day") + 1
      );
    } else if (startOffset >= timelineData.totalDays) {
      // Ticket starts after our timeline - don't show it
      adjustedStartOffset = timelineData.totalDays;
      adjustedDuration = 0;
    } else {
      // Normal case - ticket starts within our timeline
      const visibleEndDate = endDate.isBefore(timelineData.endDate)
        ? endDate
        : timelineData.endDate;
      adjustedDuration = Math.max(1, visibleEndDate.diff(startDate, "day") + 1);
    }

    // Calculate pixel positions
    const leftPosition = Math.max(
      0,
      adjustedStartOffset * timelineData.pixelsPerDay
    );
    const width = adjustedDuration * timelineData.pixelsPerDay - 8; // -8px for margin

    return {
      left: leftPosition,
      width: Math.max(timelineData.pixelsPerDay - 8, width), // Minimum width of one day minus margin
      duration: adjustedDuration,
      originalDuration: duration,
      startOffset: adjustedStartOffset,
      isClipped: startOffset < 0 || endDate.isAfter(timelineData.endDate),
    };
  };

  // Helper function to detect collisions and assign rows
  const calculateRowPositions = (items) => {
    if (!items || items.length === 0) return { items: [], maxRows: 0 };

    // Calculate positions for all items
    const itemsWithPositions = items.map((item) => ({
      ...item,
      position: calculatePostItPosition(item),
    }));

    // Sort by start date, then by priority
    itemsWithPositions.sort((a, b) => {
      const aStart = dayjs(a.createdDate);
      const bStart = dayjs(b.createdDate);
      const dateDiff = aStart.diff(bStart);

      if (dateDiff !== 0) return dateDiff; // Earlier items first

      // If same start date, sort by priority
      const priorityOrder = { ROCKS: 4, Urgent: 3, Medium: 2, Normal: 1 };
      const aPriority = priorityOrder[a.priority] || 1;
      const bPriority = priorityOrder[b.priority] || 1;
      return bPriority - aPriority; // Higher priority first
    });

    // Assign rows using collision detection
    const rows = []; // Array of arrays, each sub-array represents items in that row
    const positionedItems = [];

    itemsWithPositions.forEach((item) => {
      const itemStart = item.position.left;
      const itemEnd = item.position.left + item.position.width;

      // Find the first row where this item doesn't collide
      let assignedRow = 0;
      let foundRow = false;

      while (!foundRow) {
        if (!rows[assignedRow]) {
          rows[assignedRow] = [];
        }

        // Check if this item collides with any item in this row
        let hasCollision = false;
        for (let existingItem of rows[assignedRow]) {
          const existingStart = existingItem.position.left;
          const existingEnd =
            existingItem.position.left + existingItem.position.width;

          // Check for overlap: if itemStart < existingEnd AND itemEnd > existingStart
          if (itemStart < existingEnd && itemEnd > existingStart) {
            hasCollision = true;
            break;
          }
        }

        if (!hasCollision) {
          // No collision, place item in this row
          rows[assignedRow].push(item);
          foundRow = true;
        } else {
          // Collision detected, try next row
          assignedRow++;
        }
      }

      positionedItems.push({
        ...item,
        row: assignedRow,
        rowPosition: assignedRow * 160, // Each row is 160px tall (150px + 10px margin)
      });
    });

    return {
      items: positionedItems,
      maxRows: rows.length,
      totalHeight: rows.length * 160 + 20, // Add some padding
    };
  };

  // Group data by user and priority
  const groupedData = useMemo(() => {
    const groups = {};

    data.forEach((item) => {
      const user = item.assignedTo || "Unassigned";

      if (!groups[user]) {
        groups[user] = {
          rocks: [],
          urgent: [],
          medium: [],
          normal: [],
        };
      }

      let priorityGroup = "normal";
      const priorityCategory = item.priority || "Normal";

      // Check if this is a ROCK item
      const subject = item.name || item.description || item.subject || "";
      if (subject.startsWith("ROCK:")) {
        priorityGroup = "rocks";
      } else if (priorityCategory === "Urgent") {
        priorityGroup = "urgent";
      } else if (priorityCategory === "Medium") {
        priorityGroup = "medium";
      }

      groups[user][priorityGroup].push(item);
    });

    return groups;
  }, [data]);

  // Filter by selected user if specified
  const filteredGroups = useMemo(() => {
    if (!selectedUser || selectedUser === "All") {
      return groupedData;
    }

    return selectedUser in groupedData
      ? { [selectedUser]: groupedData[selectedUser] }
      : {};
  }, [groupedData, selectedUser]);

  // Scroll synchronization effect
  useEffect(() => {
    const bodyElement = timelineBodyRef.current;
    if (!bodyElement) return;

    console.log("🔄 Setting up scroll sync, isHeaderSticky:", isHeaderSticky);

    // Get the currently active header (either normal or sticky portal)
    const getActiveHeader = () => {
      if (isHeaderSticky) {
        // When sticky, find the portal header
        const stickyHeader = document.querySelector(
          ".timeline-header.sticky-portal"
        );
        console.log("🎯 Using sticky header for sync:", !!stickyHeader);
        return stickyHeader;
      } else {
        // When normal, use the ref
        const normalHeader =
          timelineContainerRef.current?.querySelector(".timeline-header");
        console.log("🎯 Using normal header for sync:", !!normalHeader);
        return normalHeader;
      }
    };

    const syncScrollFromHeader = (e) => {
      console.log("📜 Header scrolled, syncing to body:", e.target.scrollLeft);
      bodyElement.scrollLeft = e.target.scrollLeft;
    };

    const syncScrollFromBody = (e) => {
      const activeHeader = getActiveHeader();
      console.log(
        "📜 Body scrolled, syncing to header:",
        e.target.scrollLeft,
        "Header found:",
        !!activeHeader
      );
      if (activeHeader) {
        activeHeader.scrollLeft = e.target.scrollLeft;
      }
    };

    // Set up body scroll listener
    bodyElement.addEventListener("scroll", syncScrollFromBody);

    // Set up header scroll listener with retry mechanism
    let headerScrollCleanup = null;
    const setupHeaderListener = () => {
      const activeHeader = getActiveHeader();
      if (activeHeader) {
        console.log("✅ Setting up header scroll listener");
        activeHeader.addEventListener("scroll", syncScrollFromHeader);
        headerScrollCleanup = () => {
          activeHeader.removeEventListener("scroll", syncScrollFromHeader);
        };
      } else {
        console.log("❌ No active header found, retrying in 100ms");
        setTimeout(setupHeaderListener, 100);
      }
    };

    setupHeaderListener();

    return () => {
      console.log("🧹 Cleaning up scroll sync");
      bodyElement.removeEventListener("scroll", syncScrollFromBody);

      // Clean up current header listener
      if (headerScrollCleanup) {
        headerScrollCleanup();
      }

      // Clean up any remaining listeners on both possible headers
      const normalHeader =
        timelineContainerRef.current?.querySelector(".timeline-header");
      const stickyHeader = document.querySelector(
        ".timeline-header.sticky-portal"
      );

      if (normalHeader) {
        normalHeader.removeEventListener("scroll", syncScrollFromHeader);
      }
      if (stickyHeader) {
        stickyHeader.removeEventListener("scroll", syncScrollFromHeader);
      }
    };
  }, [isHeaderSticky]);

  // Sticky header detection for enabled sticky headers
  useEffect(() => {
    if (!enableStickyHeader) {
      console.log("❌ Sticky header not enabled");
      return;
    }

    // Use a timeout to ensure the container ref is available
    const setupObserver = () => {
      console.log("🔍 Checking sticky header setup conditions:", {
        enableStickyHeader,
        hasSentinel: !!stickySentinelRef.current,
        sentinelElement: stickySentinelRef.current,
      });

      if (!stickySentinelRef.current) {
        console.log("❌ Sticky sentinel not found, retrying in 100ms");
        setTimeout(setupObserver, 100);
        return;
      }

      console.log("✅ Setting up Intersection Observer for sticky header");

      const observer = new IntersectionObserver(
        ([entry]) => {
          const shouldBeSticky = !entry.isIntersecting;
          console.log("📍 Intersection Observer triggered:", {
            isIntersecting: entry.isIntersecting,
            shouldBeSticky,
            boundingClientRect: entry.boundingClientRect,
            intersectionRatio: entry.intersectionRatio,
            rootBounds: entry.rootBounds,
            target: entry.target,
          });
          setIsHeaderSticky(shouldBeSticky);
        },
        {
          root: null,
          rootMargin: "0px",
          threshold: [0, 0.1, 0.5, 1], // Multiple thresholds to catch more events
        }
      );

      observer.observe(stickySentinelRef.current);
      console.log("👀 Observer attached to sticky sentinel");

      // Add manual scroll listener for debugging
      const handleScroll = () => {
        const rect = timelineContainerRef.current?.getBoundingClientRect();
        console.log("📜 Manual scroll detection:", {
          scrollY: window.scrollY,
          containerTop: rect?.top,
          containerBottom: rect?.bottom,
          viewportHeight: window.innerHeight,
          isContainerVisible: rect
            ? rect.top < window.innerHeight && rect.bottom > 0
            : false,
        });
      };

      window.addEventListener("scroll", handleScroll);

      return () => {
        console.log("🧹 Cleaning up Intersection Observer and scroll listener");
        observer.disconnect();
        window.removeEventListener("scroll", handleScroll);
      };
    };

    const cleanup = setupObserver();
    return cleanup;
  }, [enableStickyHeader]);

  // Create header element for portal
  useEffect(() => {
    if (!enableStickyHeader) {
      console.log("❌ Sticky header not enabled, skipping header creation");
      return;
    }

    console.log(
      "🎨 Creating header element for portal, isHeaderSticky:",
      isHeaderSticky
    );

    // Get the exact dimensions of the original header to match
    const originalHeader =
      timelineContainerRef.current?.querySelector(".timeline-header");
    const originalRect = originalHeader?.getBoundingClientRect();

    const header = (
      <div
        className="timeline-header sticky-portal"
        style={{
          position: "fixed",
          top: 0,
          left: originalRect?.left || 0,
          width: originalRect?.width || timelineData.totalWidth,
          zIndex: 1000,
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
          transition: "all 0.2s ease",
          // Match the original header styling
          background: "#ffffff",
          borderRadius: "8px",
          padding: "10px",
          marginBottom: "20px",
        }}
        ref={timelineHeaderRef}
      >
        <div
          className="timeline-days-header"
          style={{ width: `${timelineData.totalWidth}px` }}
        >
          {timelineData.days.map((day, index) => (
            <div
              key={index}
              className={`day-header ${day.isToday ? "today" : ""} ${
                day.isWeekend ? "weekend" : ""
              }`}
              style={{
                position: "absolute",
                left: `${day.position}px`,
                width: `${timelineData.pixelsPerDay}px`,
                height: "50px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                borderRight: "1px solid rgba(0, 0, 0, 0.1)",
                backgroundColor: day.isToday
                  ? "rgba(255, 193, 7, 0.2)"
                  : day.isWeekend
                  ? "rgba(0, 0, 0, 0.05)"
                  : "transparent",
              }}
            >
              <div
                style={{ fontSize: "10px", fontWeight: "500", opacity: "0.7" }}
              >
                {day.monthName}
              </div>
              <div style={{ fontSize: "14px", fontWeight: "bold" }}>
                {day.dayNumber}
              </div>
              <div
                style={{ fontSize: "8px", fontWeight: "500", opacity: "0.6" }}
              >
                {day.date.format("ddd")}
              </div>
            </div>
          ))}
        </div>
      </div>
    );

    setHeaderElement(header);
    console.log("📦 Header element set for portal");
  }, [enableStickyHeader, timelineData, isHeaderSticky]);

  // Helper function to get priority style
  const getPriorityStyle = (priority, isCompleted) => {
    const baseStyle = {
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
      border: "2px solid",
    };

    switch (priority) {
      case "ROCKS":
        return {
          ...baseStyle,
          backgroundColor: isCompleted ? "#ffcdd2" : "#d32f2f",
          borderColor: "#b71c1c",
          color: isCompleted ? "#666" : "#fff",
        };
      case "Urgent":
        return {
          ...baseStyle,
          backgroundColor: isCompleted ? "#ffcdd2" : "#e53935",
          borderColor: "#c62828",
          color: isCompleted ? "#666" : "#fff",
        };
      case "Medium":
        return {
          ...baseStyle,
          backgroundColor: isCompleted ? "#fff3e0" : "#fb8c00",
          borderColor: "#f57c00",
          color: isCompleted ? "#666" : "#fff",
        };
      default:
        return {
          ...baseStyle,
          backgroundColor: isCompleted ? "#e8f5e8" : "#10b981",
          borderColor: "#059669",
          color: isCompleted ? "#666" : "#fff",
        };
    }
  };

  // Helper function to get priority icon
  const getPriorityIcon = (priority) => {
    if (priority === "ROCKS") return "💎";
    if (priority === "Urgent") return "🔥";
    if (priority === "Medium") return "⚡";
    return "📋";
  };

  // Helper function to check if item is completed
  const isCompleted = (item) => {
    // Handle both tickets (Closed/Solved) and tasks (Completed)
    return (
      item.status === "Closed" ||
      item.status === "Solved" ||
      item.status === "Completed"
    );
  };

  // Render priority group
  const renderPriorityGroup = (items, priorityName, priorityIcon, user) => {
    if (items.length === 0) return null;

    const positioningResult = calculateRowPositions(items);

    return (
      <div className={`postit-priority-group ${priorityName.toLowerCase()}`}>
        <div className="priority-header">
          <span className="priority-icon">{priorityIcon}</span>
          <span className="priority-label">
            {priorityName} ({items.length})
          </span>
        </div>
        <div
          className="postit-timeline-container"
          style={{
            position: "relative",
            height: `${positioningResult.totalHeight}px`,
            width: `${timelineData.totalWidth}px`,
            minHeight: "160px",
            backgroundImage: `repeating-linear-gradient(
              to right,
              rgba(0, 0, 0, 0.05),
              rgba(0, 0, 0, 0.05) 1px,
              transparent 1px,
              transparent ${timelineData.pixelsPerDay}px
            )`,
          }}
        >
          {/* Today's date indicator line */}
          {(() => {
            const today = dayjs();
            const todayOffset = today.diff(timelineData.startDate, "day");
            if (todayOffset >= 0 && todayOffset < timelineData.totalDays) {
              const leftPosition = todayOffset * timelineData.pixelsPerDay;
              return (
                <div
                  style={{
                    position: "absolute",
                    left: `${leftPosition}px`,
                    top: 0,
                    width: "5px",
                    height: "100%",
                    backgroundColor: "#FFD700", // Cold yellow
                    opacity: 0.7,
                    zIndex: 1,
                    boxShadow: "0 0 8px rgba(255, 215, 0, 0.4)",
                  }}
                  title="Today's Date"
                />
              );
            }
            return null;
          })()}
          {positioningResult.items.map((item) => {
            const itemPriority =
              priorityName === "ROCKS" ? "ROCKS" : item.priority;
            const style = getPriorityStyle(itemPriority, isCompleted(item));
            const completed = isCompleted(item);

            return (
              <div
                key={item.id}
                className={`postit-item ${priorityName.toLowerCase()} ${
                  completed ? "completed" : ""
                }`}
                style={{
                  position: "absolute",
                  left: `${item.position.left}px`,
                  top: `${item.rowPosition}px`,
                  width: `${item.position.width}px`,
                  height: "150px",
                  ...style,
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  padding: "8px",
                  fontSize: "12px",
                  fontWeight: "600",
                  transition: "all 0.2s ease",
                }}
                title={`${item.name || item.description || item.subject} - ${
                  item.priority
                } - Row ${item.row + 1}`}
                onClick={() => {
                  if (item.id) {
                    const salesforceUrl = `https://gargleinc.lightning.force.com/${item.id}`;
                    window.open(salesforceUrl, "_blank", "noopener,noreferrer");
                  }
                }}
              >
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: "700",
                    marginBottom: "8px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "normal",
                    color: "white",
                    lineHeight: "1.3",
                    maxHeight: "50px",
                  }}
                >
                  {getPriorityIcon(itemPriority)}{" "}
                  {item.name ||
                    item.description ||
                    item.subject ||
                    `Item ${item.id}`}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "white",
                    marginBottom: "6px",
                    fontWeight: "600",
                  }}
                >
                  📅 Start: {dayjs(item.createdDate).format("MMM D, YYYY")}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "white",
                    marginBottom: "8px",
                    fontWeight: "600",
                  }}
                >
                  ⏰ Due: {dayjs(item.expectedDate).format("MMM D, YYYY")}
                </div>
                <div
                  style={{
                    fontSize: "10px",
                    color: "white",
                    marginBottom: "8px",
                    fontWeight: "500",
                  }}
                >
                  📊 Status: {item.status}
                </div>
                <div
                  style={{
                    fontSize: "9px",
                    color: "white",
                    marginTop: "auto",
                    textAlign: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    borderRadius: "4px",
                    padding: "4px 6px",
                    fontWeight: "600",
                  }}
                >
                  {item.position.originalDuration || item.position.duration} day
                  {(item.position.originalDuration || item.position.duration) >
                  1
                    ? "s"
                    : ""}
                  {item.position.isClipped && " (clipped)"}
                </div>
                {completed && (
                  <div
                    style={{
                      position: "absolute",
                      top: "4px",
                      right: "4px",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    ✓
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Sticky header portal when enabled and sticky */}
      {enableStickyHeader && isHeaderSticky && headerElement && (
        <>
          {console.log("🌟 Rendering sticky header portal")}
          {createPortal(headerElement, document.body)}
        </>
      )}

      <div className="postit-timeline-view" ref={timelineContainerRef}>
        {/* Sticky sentinel - invisible element to detect when header should become sticky */}
        <div
          ref={stickySentinelRef}
          style={{
            height: "1px",
            width: "100%",
            position: "absolute",
            top: "0",
            left: "0",
            pointerEvents: "none",
            visibility: "hidden",
          }}
        />

        {/* Timeline Header - hidden when sticky portal is active */}
        <div
          className="timeline-header"
          ref={!enableStickyHeader ? timelineHeaderRef : null}
          style={{
            visibility:
              enableStickyHeader && isHeaderSticky ? "hidden" : "visible",
          }}
        >
          <div
            className="timeline-days-header"
            style={{ width: `${timelineData.totalWidth}px` }}
          >
            {timelineData.days.map((day, index) => (
              <div
                key={index}
                className={`day-header ${day.isToday ? "today" : ""} ${
                  day.isWeekend ? "weekend" : ""
                }`}
                style={{
                  position: "absolute",
                  left: `${day.position}px`,
                  width: `${timelineData.pixelsPerDay}px`,
                  height: "50px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRight: "1px solid rgba(0, 0, 0, 0.1)",
                  backgroundColor: day.isToday
                    ? "rgba(255, 193, 7, 0.2)"
                    : day.isWeekend
                    ? "rgba(0, 0, 0, 0.05)"
                    : "transparent",
                }}
              >
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: "500",
                    opacity: "0.7",
                  }}
                >
                  {day.monthName}
                </div>
                <div style={{ fontSize: "14px", fontWeight: "bold" }}>
                  {day.dayNumber}
                </div>
                <div
                  style={{ fontSize: "8px", fontWeight: "500", opacity: "0.6" }}
                >
                  {day.date.format("ddd")}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline Body */}
        <div className="timeline-body" ref={timelineBodyRef}>
          {Object.entries(filteredGroups).map(([user, userGroups]) => {
            const totalItems =
              userGroups.rocks.length +
              userGroups.urgent.length +
              userGroups.medium.length +
              userGroups.normal.length;

            if (totalItems === 0) return null;

            return (
              <div key={user} className="user-section">
                <div className="user-header">
                  <h4>{user}</h4>
                  <span className="item-count">
                    ({totalItems} {type})
                  </span>
                </div>

                <div className="user-timeline-content">
                  {renderPriorityGroup(userGroups.rocks, "ROCKS", "💎", user)}
                  {renderPriorityGroup(
                    userGroups.urgent,
                    "Urgent & Time-Sensitive",
                    "🔥",
                    user
                  )}
                  {renderPriorityGroup(userGroups.medium, "Not Time-Sensitive", "⚡", user)}
                  {renderPriorityGroup(userGroups.normal, "Normal", "📋", user)}
                </div>
              </div>
            );
          })}

          {Object.keys(filteredGroups).length === 0 && (
            <div className="empty-state">
              <p>No {type} found for the selected user.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PostItTimelineView;
